import { useState } from 'react';
import type { TagRef, TagTarget } from '@pb/shared';
import { setTags } from '../api/tags';
import { useProject } from '../lib/project';
import { Dialog } from './Dialog';
import { ErrorAlert } from './States';

export function TagList({ tags }: { tags: TagRef[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="tags">
      {tags.map((tag) => (
        <span className="tag" key={tag.id}>
          {tag.name}
        </span>
      ))}
    </div>
  );
}

export function TagFilter({ selected, onChange }: { selected: number[]; onChange: (ids: number[]) => void }) {
  const { tags } = useProject();
  if (tags.length === 0) return null;
  return (
    <div className="row-wrap" role="group" aria-label="Filter by tags (all must match)">
      <span className="small muted">Tags:</span>
      {tags.map((tag) => {
        const on = selected.includes(tag.id);
        return (
          <button
            key={tag.id}
            type="button"
            className="chip"
            aria-pressed={on}
            onClick={() => onChange(on ? selected.filter((id) => id !== tag.id) : [...selected, tag.id])}
          >
            {tag.name}
          </button>
        );
      })}
      {selected.length ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([])}>
          Clear
        </button>
      ) : null}
    </div>
  );
}

interface TagEditorDialogProps {
  target: TagTarget;
  ids: number[];
  initial: number[];
  title: string;
  hint?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function TagEditorDialog({ target, ids, initial, title, hint, onClose, onSaved }: TagEditorDialogProps) {
  const { tags, reloadTags } = useProject();
  const [chosen, setChosen] = useState<number[]>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await setTags(target, ids, chosen);
      await reloadTags();
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save tags'}
          </button>
        </>
      }
    >
      {hint ? <p className="small muted">{hint}</p> : null}
      {tags.length === 0 ? (
        <p className="muted">
          This project has no tags yet. Create tags with the “Tags” button in the project header.
        </p>
      ) : (
        <div className="row-wrap" role="group" aria-label="Tags">
          {tags.map((tag) => {
            const on = chosen.includes(tag.id);
            return (
              <label key={tag.id} className="checkbox chip" data-on={on}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => setChosen(on ? chosen.filter((id) => id !== tag.id) : [...chosen, tag.id])}
                  aria-label={tag.name}
                />
                {tag.name}
              </label>
            );
          })}
        </div>
      )}
      <ErrorAlert error={error} />
    </Dialog>
  );
}
